import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/services/api';

/**
 * The monolith kept every view fresh with one global 2-second setInterval
 * that refetched everything, visible or not. React Query replaces that with
 * per-query caching plus push-driven invalidation, so the defaults here are
 * tuned for "something will tell us when this is stale":
 *
 * - staleTime 15s — long enough that switching tabs back and forth does not
 *   re-hit the API, short enough that a missed push self-corrects quickly.
 * - refetchOnWindowFocus — a dispatcher alt-tabbing back into the console
 *   should see current data immediately; that is exactly the moment an
 *   extra request is worth making.
 * - retry — never retry a 4xx (a 401 has already signed the user out, and a
 *   404 will not become a 200), twice for everything else, since the
 *   backend is a single Flask process that does drop requests across a
 *   restart.
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        // services/api.js already retries transient failures with backoff,
        // so by the time an error reaches React Query the request has been
        // tried three times. Retrying again here would turn one slow request
        // into nine. This layer only catches what the client deliberately
        // does not retry — chiefly non-idempotent calls — and gives up fast
        // on anything a 4xx says will never succeed.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && !error.isRetryable) return false;
          return failureCount < 1;
        },
      },
      mutations: { retry: false },
    },
  });
}
